<template lang="pug">
div
  aiq-dialog.edit-conversation-review(:model-value="true"
            :title="title"
            :close-on-click-modal="true"
            :close-on-press-escape="true"
            @close="close"
            :before-close="handleBeforeClose"
            destroy-on-close)
    .form
      .field-container
        label {{ $t(`${translationBasePath}.lynq_id`) }}
        span {{ selectedReview.conversation.customer_id }}

      .field-container
        label {{ $t(`${translationBasePath}.conversation_start_date`) }}
        span {{ formatDate(selectedReview.conversation.created_at) }}

      .field-container
        label
          span {{ $t(`${translationBasePath}.agents_who_contributed.label`) }}
          aiq-tooltip(placement="bottom"
                    popper-class="capacity-explanation-popup"
                    :content="$t(`${translationBasePath}.agents_who_contributed.tooltip`)")
            fa-icon.info-icon(:icon="['fas', 'circle-question']")

        .agents-container(v-if="selectedReview.agents_who_contributed.length")
          aiq-tag(v-for="agent in selectedReview.agents_who_contributed" :key="agent.id") {{ agent.fullName }}
        span(v-else) {{ $t(`${this.translationBasePath}.not_available`) }}

      .field-container
        label {{ $t(`${translationBasePath}.summary`) }}
        span {{ summary }}

      .field-container(v-if="editMode || readOnlyMode")
        label {{ $t(`${translationBasePath}.reviewer`) }}
        span {{ reviewerName }}

      .field-container
        label {{ $t(`${translationBasePath}.review_date`) }}
        span {{ formatDate(selectedReview.created_at) }}

      .field-container.is-required
        label {{ $t(`${translationBasePath}.rating`) }}
        div
          aiq-rate(v-model="form.rating" :disabled="readOnlyMode")

      .field-container.full-width
        label {{ $t(`${translationBasePath}.comment`) }}
        aiq-input(v-model="form.comment"
                  type="textarea"
                  :rows="5"
                  :disabled="readOnlyMode")

    template(v-slot:footer)
      p.required-notice * {{ $t(`${translationBasePath}.required`) }}
      span.spinner
        font-awesome-icon(v-if="isBusy"
                          size="lg"
                          icon="spinner"
                          spin)

      aiq-button.button-delete(v-if="editMode"
                              type="danger"
                              :disabled="isBusy"
                              @click="handleBeforeDelete") {{ $t(`${translationBasePath}.delete`) }}
      aiq-button(v-if="readOnlyMode" @click="close") {{ $t(`${translationBasePath}.close`) }}
      template(v-else)
        aiq-button(@click="close" :disabled="isBusy") {{ $t(`${translationBasePath}.cancel`) }}
        aiq-button(:disabled="!isSubmittable || isBusy"
                  type="primary"
                  @click="saveRecord") {{ $t(`${translationBasePath}.save`) }}
</template>

<script>
import { mapState, mapActions } from 'vuex';
import get from 'lodash/get';
import has from 'lodash/has';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import dayjs from '@/localPlugins/dayjs';

import { confirmBeforeDialogClose } from '@/libs';
import SummaryMixin from '@/libs/mixins/Summary';

const FORM_ACTIONS = {
  UPDATE: 'update',
  CREATE: 'create',
  READ_ONLY: 'read-only',
};

export default {
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      form: {
        rating: { $autoDirty: true, required },
        comment: { $autoDirty: true },
      },
    };
  },
  mixins: [SummaryMixin],
  data() {
    const form = this.getInitialForm();
    return {
      form,
      isBusy: false,
      summary: '',
      isLoadingSummary: false,
    };
  },
  async mounted() {
    const conversationId = this.selectedReview.conversation.id;
    if (this.getIsSummarizable(conversationId) && this.canSummarize) {
      await this.onSummary(conversationId);
    } else {
      this.summary = this.$t(`${this.translationBasePath}.not_available`);
    }

    if (this.editMode || this.readOnlyMode) {
      this.form = {
        rating: this.selectedReview.rating,
        comment: this.selectedReview.comment,
      };
      this.$nextTick(() => {
        if (this.v$) {
          this.v$.$reset();
        }
      });
    }
  },
  computed: {
    ...mapState({
      selectedReview: state => state.conversationReviews.selectedReview,
      agentProfile: state => state.agent.profile,
    }),
    action() {
      if (has(this.selectedReview, 'id')) {
        if (get(this.agentProfile, 'id') === get(this.selectedReview, 'reviewer.id')) {
          return FORM_ACTIONS.UPDATE;
        }
        return FORM_ACTIONS.READ_ONLY;
      }
      return FORM_ACTIONS.CREATE;
    },
    editMode() {
      return this.action === FORM_ACTIONS.UPDATE;
    },
    readOnlyMode() {
      return this.action === FORM_ACTIONS.READ_ONLY;
    },
    title() {
      let prefix;
      if (this.editMode) {
        prefix = 'Update ';
      } else if (this.readOnlyMode) {
        prefix = '';
      } else {
        prefix = 'New ';
      }
      return `${prefix} Conversation Review`;
    },
    isRecordDirty() {
      if (this.v$) {
        return this.v$.$anyDirty;
      }
      return true;
    },
    isRecordValid() {
      if (this.v$) {
        return !this.v$.$invalid;
      }
      return true;
    },
    isSubmittable() {
      return (this.isRecordDirty && this.isRecordValid);
    },
    primaryAgentName() {
      return get(this.selectedReview, 'primary_agent.fullName', this.$t(`${this.translationBasePath}.not_available`));
    },
    reviewerName() {
      return get(this.selectedReview, 'reviewer.fullName', this.$t(`${this.translationBasePath}.not_available`));
    },
    translationBasePath() {
      return 'conversation_tab.chat_panel.conversation_reviews.review_dialog';
    },
  },
  methods: {
    ...mapActions({
      createReview: 'conversationReviews/createReview',
      updateReview: 'conversationReviews/updateReview',
      deleteReview: 'conversationReviews/deleteReview',
      closeDialog: 'conversationReviews/closeDialog',
      refetchReviews: 'conversationReviews/refetchReviews',
    }),
    getInitialForm() {
      return {
        rating: 0,
        comment: '',
      };
    },
    formatDate(date) {
      return dayjs(date).format('MMM DD, YYYY hh:mm A');
    },
    close() {
      this.clearForm();
      this.closeDialog();
    },
    async saveRecord() {
      try {
        this.isBusy = true;
        if (this.editMode) {
          await this.updateReview({
            rating: this.form.rating,
            comment: this.form.comment,
          });
        } else {
          await this.createReview({
            rating: this.form.rating,
            comment: this.form.comment,
          });
        }
        await this.refetchReviews();
        this.$aiq.notify.success(this.$t(`${this.translationBasePath}.notify.${this.action === FORM_ACTIONS.CREATE ? 'create_success_msg' : 'update_success_msg'}`));
        this.close();
      } catch (e) {
        this.$aiq.notify.error(this.$t(`${this.translationBasePath}.notify.${this.action === FORM_ACTIONS.CREATE ? 'create_error_msg' : 'update_error_msg'}`));
      } finally {
        this.isBusy = false;
      }
    },
    async handleBeforeDelete() {
      try {
        await this.$aiq.confirm(
          this.$t(`${this.translationBasePath}.confirm.title`),
          this.$t(`${this.translationBasePath}.confirm.body`),
          {
            confirmButtonText: this.$t(`${this.translationBasePath}.confirm.confirm_btn`),
            cancelButtonText: this.$t(`${this.translationBasePath}.confirm.cancel_btn`),
            type: 'warning',
          },
        );
        await this.deleteRecord();
      } catch (_) {
        // This is left empty because when the user clicks cancel nothing happens
      }
    },
    async deleteRecord() {
      try {
        this.isBusy = true;
        await this.deleteReview();
        await this.refetchReviews();
        this.$aiq.notify.success(this.$t(`${this.translationBasePath}.notify.delete_success_msg`));
        this.close();
      } catch (_) {
        this.$aiq.notify.error(this.$t(`${this.translationBasePath}.notify.delete_error_msg`));
      } finally {
        this.isBusy = false;
      }
    },
    handleBeforeClose() {
      if (!this.isRecordDirty) {
        this.close();
        return;
      }

      confirmBeforeDialogClose(this, this.close);
    },
    clearForm() {
      this.form = this.getInitialForm();
      this.$nextTick(() => {
        if (this.v$) {
          this.v$.$reset();
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/media-queries.scss";

.edit-conversation-review.el-dialog {
  margin-top: 5vh !important;
  min-width: 520px;
  @include mobile {
    min-width: 90%;
  }
  .el-dialog__body {
    padding-bottom: 0;
    .field-container {
      display: flex;
      align-items: start;
      margin: 15px 0;
      > :first-child {
        flex: 1;
      }
      > :nth-child(2) {
        flex: 2;
      }
      label {
        font-size: 14px;
        font-weight: 100;
      }
      & > *, .el-textarea__inner {
        font-size: 12px;
        font-weight: 300;
      }
      .agents-container {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
      .el-rate {
        position: relative;
        top: 5px;
        left: -5px;
      }
      &.full-width {
        display: block;
        :nth-child(2) {
          margin-top: 5px;
        }
      }
      &.is-required {
        label {
          &::before {
            margin-right: 5px;
            content: "*";
            color: #FF0000;
          }
        }
      }
      .info-icon {
        margin-left: 5px;
      }
    }
  }
  .el-dialog__footer {
    p {
      width: 100%;
      margin-top: 10px;
      margin-bottom: 8px;
      color: #FF0000;
      font-size: 14px;
      font-weight: 100;
    }
  }
  .spinner {
    padding-right: 0.5em;
  }
}
</style>
